import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  CardHeader,
  Tooltip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  ButtonProps
} from '@mui/material'
import { Upload, Visibility } from '@mui/icons-material'
import { RootState } from '../redux'
import { getLevelsByProjectId } from '../redux/slices/levels'
import useAuth from '../hooks/useAuth'
import useLevels from '../hooks/useLevels'
import useLevel from '../hooks/useLevel'
import { usePolicy } from '../contexts/PolicyContext'
import EmptyState from './EmptyState'
import LoadingState from './LoadingState'
import UploadType from '../enums/UploadType'
import { createUpload, removeUpload } from '../redux/slices/uploads'
import { deleteError, setError } from '../redux/slices/errors'
import { UploadErrors } from '../enums/UploadErrors'
import { createErrorOrSuccessNotification } from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import { createUserNotification } from '../utils/createUserNotification'
import uploadService, { getBlueprintService } from '../services/uploadService'
import { Level, Project } from '../types'
import { ProjectStateType } from '../enums/ProjectStateType'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { UploadWithLink } from '../types/Upload'
import { IconButtonProps } from 'material-ui'
import LevelSelect from './LevelSelect'
import LevelDeleteButton from './LevelDeleteButton'
import LevelEditButton from './LevelEditButton'
import LevelCreateButton from './LevelCreateButton'
import Dropzone, { DropzoneProps } from './Dropzone'

interface ButtonOrIconButtonProps extends Omit<ButtonProps, 'component'> {
  component: typeof IconButton | typeof Button
}

function ButtonOrIconButton({ component, ...props }: ButtonOrIconButtonProps) {
  return <>{component === IconButton ? <IconButton {...props} /> : <Button {...props} />}</>
}

interface UploadPreviewProps {
  upload: UploadWithLink
}

function UploadPreview({ upload }: UploadPreviewProps) {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      sx={{ backgroundColor: 'white' }}
    >
      {upload.blueprint.mimeType.startsWith('image') && <img src={upload.url} />}
    </Box>
  )
}

interface UploadBlueprintButtonProps extends Omit<ButtonProps, 'component' | 'onClick'> {
  project: Project
  level?: Level
  blueprint?: UploadWithLink
  afterUpload?: (upload: UploadWithLink) => void
  component?: typeof IconButton | typeof Button
}

function UploadBlueprintButton({
  project,
  level,
  blueprint,
  children,
  component = Button,
  afterUpload,
  ...props
}: UploadBlueprintButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * Indicates if the blueprint dialog is open.
   */
  const [isBlueprintDialogOpen, setIsBlueprintDialogOpen] = useState(false)

  /**
   * The ID of the selected level.
   */
  const [selectedLevelId, setSelectedLevelId] = useState<number | undefined>(
    level !== undefined ? level.id : undefined
  )

  /**
   * The upload.
   */
  const [upload, setUpload] = useState<UploadWithLink | undefined>(blueprint)

  /**
   * The active step.
   */
  const [activeStep, setActiveStep] = useState(level !== undefined ? 1 : 0)

  /**
   * Indicates if the blueprint is being uploaded.
   */
  const [_isUploading, setIsUploading] = useState(false)

  /**
   * Open the blueprint dialog.
   */
  function openBlueprintDialog() {
    setIsBlueprintDialogOpen(true)
  }

  /**
   * Close the blueprint dialog.
   */
  function closeBlueprintDialog() {
    setIsBlueprintDialogOpen(false)
    setActiveStep(level !== undefined ? 1 : 0)
    setUpload(blueprint ?? undefined)
    setSelectedLevelId(level !== undefined ? level.id : undefined)
  }

  /**
   * Load the level blueprint.
   */
  async function loadLevelBlueprint(level: number): Promise<void> {
    try {
      setUpload(
        await getBlueprintService({
          project: project.id,
          level: level
        })
      )
    } catch (_error) {
      setUpload(undefined)
    }
  }

  /**
   * Load the area blueprint.
   */
  async function loadAreaBlueprint(): Promise<void> {
    try {
      setUpload(await getBlueprintService({ project: project.id }))
    } catch (_error) {
      setUpload(undefined)
    }
  }

  /**
   * Handle the file drop.
   */
  const handleDrop: DropzoneProps['onDrop'] = useCallback(
    (files) => {
      if (files.length > 0 && files[0].type === 'application/pdf') {
        uploadBlueprint(files[0])
      }
    },
    [selectedLevelId]
  )

  /**
   * Delete the blueprint.
   */
  async function deleteBlueprint() {
    if (upload !== undefined) {
      await uploadService.deleteUpload(upload.blueprint.id)
      dispatch(removeUpload(upload.blueprint.id))
      setUpload(undefined)

      // Reload levels so that the blueprint gets removed from the list.
      if (selectedLevelId !== undefined) {
        dispatch(getLevelsByProjectId(project.id))
      } else {
        // TODO: Refresh area blueprint.
      }
    }
  }

  /**
   * Upload a blueprint.
   */
  async function uploadBlueprint(file: File) {
    try {
      setIsUploading(true)

      const name =
        selectedLevelId !== undefined
          ? `${UploadType.BLUEPRINT}/project/${project.id}/level/${selectedLevelId}`
          : `${UploadType.BLUEPRINT}/project/${project.id}/area`

      const formData = new FormData()

      formData.append('file', file)
      formData.append('name', name)

      const upload = await uploadService.postUpload(formData)
      dispatch(createUpload(upload))

      // Reload levels so that the blueprint gets added to the list.
      if (selectedLevelId !== undefined) {
        dispatch(getLevelsByProjectId(project.id))
      } else {
        const upload = await getBlueprintService({ project: project.id })
        afterUpload?.(upload)
      }

      // Go to the final step.
      setActiveStep(2)

      dispatch(createErrorOrSuccessNotification(NotificationType.SUCCESS, t('uploadCreateSuccess')))
    } catch (error: unknown) {
      dispatch(
        setError({
          type: UploadErrors.CREATE,
          error: error
        })
      )

      const errorMessage = createUserNotification({
        user: currentUser,
        type: UploadErrors.CREATE,
        error: error
      })

      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )
      // TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(UploadErrors.CREATE))
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Load the blueprint when level id changes.
   */
  useEffect(() => {
    setUpload(undefined)

    if (selectedLevelId !== undefined) {
      loadLevelBlueprint(selectedLevelId)
    } else {
      loadAreaBlueprint()
    }
  }, [selectedLevelId])

  /**
   * Load the blueprint when the active step changes.
   */
  useEffect(() => {
    if (activeStep === 2) {
      if (selectedLevelId !== undefined) {
        loadLevelBlueprint(selectedLevelId)
      } else {
        loadAreaBlueprint()
      }
    }
  }, [activeStep])

  return (
    <>
      <ButtonOrIconButton component={component} {...props} onClick={() => openBlueprintDialog()}>
        {children ? children : t('uploadBlueprint')}
      </ButtonOrIconButton>

      <Dialog
        open={isBlueprintDialogOpen}
        maxWidth="lg"
        fullWidth
        onClose={() => closeBlueprintDialog()}
      >
        <DialogTitle>{t('uploadBlueprint')}</DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
            <Step key={1}>
              <StepLabel>{t('selectLevelOrArea')}</StepLabel>
            </Step>
            <Step key={2}>
              <StepLabel>{t('uploadFile')}</StepLabel>
            </Step>
            <Step key={3}>
              <StepLabel>{t('completed')}</StepLabel>
            </Step>
          </Stepper>

          {activeStep === 0 && (
            <>
              <Alert severity="info" variant="filled" sx={{ mb: 6 }}>
                {t('selectLevelOrAreaHelper')}
              </Alert>

              <Stack direction="column" spacing={2}>
                <LevelSelect
                  value={selectedLevelId ?? null}
                  fullWidth
                  onChange={(event) => {
                    const value = event.target.value

                    if (value === null || value === undefined) {
                      setSelectedLevelId(undefined)
                    } else {
                      const numValue = parseInt(value?.toString())
                      if (!isNaN(numValue)) {
                        setSelectedLevelId(numValue)
                      }
                    }
                  }}
                />
              </Stack>

              <Stack direction="row" justifyContent="flex-end" mt={3} spacing={2}>
                <Button onClick={() => closeBlueprintDialog()}>{t('cancel')}</Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setActiveStep((prevActiveStep) => prevActiveStep + 1)}
                >
                  {t('next')}
                </Button>
              </Stack>
            </>
          )}

          {activeStep === 1 && (
            <>
              {upload !== undefined ? (
                <>
                  <Alert severity="warning" sx={{ mb: 4 }}>
                    {selectedLevelId !== undefined
                      ? t('Project.deleteBlueprint')
                      : t('Project.deleteAreaBlueprint')}
                  </Alert>

                  <UploadPreview upload={upload} />

                  <Stack direction="row" justifyContent="flex-end" mt={3} spacing={2}>
                    <Button onClick={() => setActiveStep((prevActiveStep) => prevActiveStep - 1)}>
                      {t('previous')}
                    </Button>
                    <Button
                      onClick={() =>
                        navigate(
                          `/blueprint/project/${project.id}/level/${
                            selectedLevelId !== undefined ? selectedLevelId : 'area'
                          }`
                        )
                      }
                    >
                      {t('manageDevices')}
                    </Button>
                    <Button variant="text" color="error" onClick={() => deleteBlueprint()}>
                      {t('deleteBlueprint')}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => closeBlueprintDialog()}
                    >
                      {t('complete')}
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  <Dropzone
                    label={t('uploadBlueprintAsPdf')}
                    buttonText={t('selectFileFromYourDevice')}
                    minHeight={200}
                    height="66vh"
                    accept={{
                      'application/pdf': ['.pdf']
                    }}
                    onDrop={handleDrop}
                  />

                  <Stack direction="row" justifyContent="flex-end" mt={3} spacing={2}>
                    <Button onClick={() => setActiveStep((prevActiveStep) => prevActiveStep - 1)}>
                      {t('previous')}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={true}
                      onClick={() => closeBlueprintDialog()}
                    >
                      {t('complete')}
                    </Button>
                  </Stack>
                </>
              )}
            </>
          )}

          {activeStep === 2 && (
            <>
              {upload === undefined ? <LoadingState /> : <UploadPreview upload={upload} />}

              <Stack direction="row" justifyContent="flex-end" mt={3} spacing={2}>
                <Button onClick={() => setActiveStep((prevActiveStep) => prevActiveStep - 1)}>
                  {t('previous')}
                </Button>
                <Button
                  onClick={() =>
                    navigate(
                      `/blueprint/project/${project.id}/level/${
                        selectedLevelId !== undefined ? selectedLevelId : 'area'
                      }`
                    )
                  }
                >
                  {t('manageDevices')}
                </Button>
                <Button variant="contained" color="success" onClick={() => closeBlueprintDialog()}>
                  {t('complete')}
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

interface LevelBlueprintIconButtonProps extends Omit<IconButtonProps, 'onClick'> {
  level: Level
}

function LevelBlueprintIconButton({ level, ...props }: LevelBlueprintIconButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The blueprint.
   */
  const [blueprint, setBlueprint] = useState<UploadWithLink>()

  /**
   * Load the blueprint.
   */
  async function loadBlueprint(): Promise<void> {
    if (level.projectId !== null) {
      try {
        setBlueprint(
          await getBlueprintService({
            project: level.projectId,
            level: level.id
          })
        )
      } catch (_error) {
        setBlueprint(undefined)
      }
    }
  }

  /**
   * Handle the click event.
   */
  function handleClick() {
    navigate(`/blueprint/project/${level.projectId}/level/${level.id}`)
  }

  /**
   * Attempt to load the level blueprint when the component mounts.
   */
  useEffect(() => {
    loadBlueprint()
  }, [level])

  return (
    <>
      {blueprint && (
        <Tooltip title={t('showBlueprint') as string}>
          {/* @ts-expect-error typed incorrectly */}
          <IconButton {...props} onClick={handleClick}>
            <Visibility fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}

export interface LevelTableProps {
  project?: Project
}

export default function LevelTable({ project }: LevelTableProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The levels.
   */
  const levels: Level[] = useLevels()

  const level: Level | undefined = useLevel()

  const isLoading: boolean = useSelector((state: RootState) => state.levels.loading)

  const isShowing: boolean = useSelector((state: RootState) => state.levels.show)

  const isEmpty: boolean = levels.length === 0

  const title = !isShowing ? t('levels') : level === undefined ? t('newLevel') : t('editLevel')

  const [upload, setUpload] = useState<UploadWithLink | undefined>(undefined)

  async function loadAreaBlueprint(project: number): Promise<void> {
    try {
      setUpload(await getBlueprintService({ project: project }))
    } catch (_error) {
      setUpload(undefined)
    }
  }

  /**
   * The grid columns.
   */
  const columns: GridColDef<Level>[] = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      width: 200,
      editable: false
    },
    {
      field: 'number',
      headerName: t('number'),
      type: 'number',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      width: 200,
      editable: false,
      valueGetter: (_, row) => (row.number !== null && row.number > -999 ? row.number : '-')
    },
    {
      field: t('actions'),
      type: 'actions',
      headerName: t('actions'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1} height="100%">
          {/*project && (
                <Tooltip title={t('addFloorPlan') as string}>
                  <IconButton
                    size="small"
                    disabled={project?.state === ProjectStateType.ARCHIVED}
                    onClick={() => handleClickOpenLevelBlueprintForm(params.row)}
                  >
                    <UploadFile fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}*/}

          {/*project && (
                <UploadBlueprintButton
                  component={IconButton}
                  project={project}
                  level={params.row}
                  size="small"
                  disabled={project?.state === ProjectStateType.ARCHIVED}
                >
                  <UploadFile fontSize="inherit" />
                </UploadBlueprintButton>
              </Tooltip>
            )*/}

          {/* @ts-expect-error typed incorrectly*/}
          <LevelBlueprintIconButton level={params.row} size="small" />
          <LevelEditButton project={project} level={params.row} />
          <LevelDeleteButton project={project} level={params.row} />
        </Stack>
      )
    }
  ]

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0
  })

  /**
   * Load the area blueprint the project changes.
   */
  useEffect(() => {
    if (project) {
      loadAreaBlueprint(project.id)
    }
  }, [project])

  return (
    <Card>
      <CardHeader
        title={title}
        action={
          <Stack direction="row" gap={3}>
            {project && upload && (
              <Button
                variant="text"
                onClick={() => navigate(`/blueprint/project/${project.id}/level/area`)}
              >
                {t('viewAreaBlueprint')}
              </Button>
            )}

            {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
              <UploadBlueprintButton
                project={project}
                variant="text"
                endIcon={<Upload />}
                afterUpload={(upload) => {
                  setUpload(upload)
                }}
              />
            )}

            <LevelCreateButton project={project} />
          </Stack>
        }
      />
      <CardContent>
        {isLoading ? (
          <LoadingState />
        ) : isEmpty ? (
          <EmptyState title={t('noLevels')} />
        ) : (
          <DataGrid
            rows={levels}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableRowSelectionOnClick
          />
        )}
      </CardContent>
    </Card>
  )
}
